import { CoreApi, PlannerProductComponent, Product } from '@inter-ikea-storageone/range';
import { useInsightsML } from 'contexts/insightsML';
import { getAllValidProducts } from 'ducks/products';
import { useSelector } from 'react-redux';
import { getSelectedEntity } from 'ducks/core';
import { useCore } from 'contexts/core';
import { HierarchyUtils, Entity } from '@inter-ikea-core/core';
import { getEntityFrame } from 'analytics-api/payload-transformers/withSceneArticles';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

type MLSort = {
  loading: boolean;
  sortedProducts: Product[];
};

const stripDexfPrefix = (globalItemNo: string) =>
  globalItemNo
    .replace('ART-', '')
    .replace('SPR-', '')
    .replace('MSC-', '');

const isProductEntity = (entity: Entity) => entity.hasComponent(PlannerProductComponent.id);

const getSortData = (coreApi: CoreApi, selectedEntityId: number) => {
  const allEntitiesInScene = coreApi.entities.getAll();
  const selectedEntity = allEntitiesInScene.find(e => e.id === selectedEntityId);
  const selectedEntityFrame = selectedEntity && getEntityFrame(selectedEntity);

  if (!selectedEntity) return null;

  const sceneEntityRefs = allEntitiesInScene.map(coreApi.entities.getProductRef) as string[];
  const selectedEntityRef = coreApi.entities.getProductRef(selectedEntity) as string;
  const frameEntityRefs = (selectedEntityFrame
    ? HierarchyUtils.descendants(selectedEntityFrame)
        .filter(isProductEntity)
        .map(coreApi.entities.getProductRef)
    : []) as string[];

  return { sceneEntityRefs, selectedEntityRef, frameEntityRefs };
};

const useMLSortSelected = (replaceableProducts: Product[], enabled: boolean): MLSort => {
  const coreApi = useCore();
  const mlService = useInsightsML();
  const selectedProductEntity = useSelector(getSelectedEntity);
  const allProducts = useSelector(getAllValidProducts);

  const replaceableProductIds = replaceableProducts.map(p => p.coreProductId);

  // true if replaceableProducts contains selected
  // entity, otherwise selected entity productId
  const selectedReference = selectedProductEntity
    ? replaceableProductIds.includes(selectedProductEntity.productId) ||
      selectedProductEntity.productId
    : null;

  const allOtherArticles = coreApi.entities
    .getAll()
    .filter(e => e.id !== selectedProductEntity?.entityId)
    .map(coreApi.entities.getGlobalArticleNumber)
    .filter(id => !replaceableProducts.map(p => p.globalItemNo).includes(id as string));

  const globalItemNoToReplacableProduct = (globalItemNo: string) =>
    replaceableProducts.find(
      prod => stripDexfPrefix(prod.globalItemNo) === stripDexfPrefix(globalItemNo)
    );

  const { data, isLoading, refetch } = useQuery({
    enabled,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryKey: [
      selectedReference,
      replaceableProductIds,
      selectedReference !== true && allOtherArticles
    ],
    queryFn: async () => {
      if (!selectedProductEntity) return;

      const sortData = getSortData(coreApi, selectedProductEntity.entityId);

      if (!sortData) return;

      const { sceneEntityRefs, selectedEntityRef, frameEntityRefs } = sortData;

      const filter = replaceableProducts
        // Remove selected product from filter
        .filter(p => p.coreProductId !== selectedProductEntity.productId)
        .map(p => stripDexfPrefix(p.globalItemNo));

      const data = await mlService.fetchSwapCommendations(
        sceneEntityRefs,
        frameEntityRefs,
        selectedEntityRef,
        filter
      );

      const selectedProduct = allProducts.find(
        p => p.coreProductId === selectedProductEntity.productId
      ) as Product;

      const notInResponse = replaceableProducts.filter(({ globalItemNo }) => {
        const currId = stripDexfPrefix(globalItemNo);
        const responseIds = data.map(stripDexfPrefix);
        const selectedId = stripDexfPrefix(selectedProduct.globalItemNo);
        return selectedId !== currId && !responseIds.includes(currId);
      });

      const products = [selectedProduct.globalItemNo, ...data]
        .map(globalItemNoToReplacableProduct)
        .filter(Boolean) as Product[];

      return [...products, ...notInResponse];
    }
  });

  useEffect(() => {
    // Always trigger a fetch on render
    enabled && !isLoading && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading: isLoading, sortedProducts: data ?? [] };
};

export default useMLSortSelected;
